// HU-47 · Mantener los destinatarios de los avisos por correo. F-02, Sprint 1.
//
// Cuatro endpoints, y la forma de los dos ultimos dice algo del diseno: pausar y
// reactivar son POST a rutas propias, no un `PUT {activo: false}`. Son ACTOS con
// consecuencia —dejar de avisar a alguien— y quedan en la auditoria como tales.

import { pedir, seg, segUuid } from './cliente';
import type { RespuestaDelApi } from '../tipos';

/**
 * HU-47: "veo cada destinatario con su nombre, cargo, correo, ambito y que avisos recibe".
 *
 * Los pausados vienen INCLUIDOS por defecto, y es deliberado: ocultarlos haria
 * imposible reactivarlos desde la pantalla.
 */
export function listarDestinatarios({ incluirPausados = true }: { incluirPausados?: boolean } = {}) {
  return pedir<RespuestaDelApi[]>(`/destinatarios-de-aviso?incluir_pausados=${seg(incluirPausados)}`);
}

type DatosDeDestinatario = {
  tipoAviso: string;
  correo: string;
  nombre: string;
  cargo: string;
  /** Nulo o ausente = ambito global; con codigo ISO = solo ese pais. */
  pais?: string | null;
};

export function crearDestinatario({ tipoAviso, correo, nombre, cargo, pais }: DatosDeDestinatario) {
  return pedir('/destinatarios-de-aviso', {
    metodo: 'POST',
    cuerpo: {
      tipo_aviso: tipoAviso,
      correo,
      nombre,
      cargo,
      // `pais: null` NO es un olvido: es "todos los paises" (RN-02, RNF-08). Un
      // destinatario con ambito Chile no recibe un caso de Peru; uno sin pais, si.
      pais: pais || null,
    },
  });
}

/**
 * CP-47-04 · EL AMBITO NO SE GUARDABA (5-sep-2026).
 *
 * El servidor exige `cambiar_ambito` para tocar el pais, y con razon: `pais: null` ya
 * significa "todos los paises", asi que sin una bandera aparte no hay forma de distinguir
 * "pasalo a ambito global" de "no lo toques". Este cliente no la mandaba nunca, asi que el
 * pais viajaba y se descartaba EN SILENCIO: el administrador cambiaba el ambito, pulsaba
 * Guardar, no veia ningun error, y el cambio no se aplicaba.
 *
 * `cambiarAmbito` lo decide QUIEN LLAMA comparando con el valor que tenia la fila, y no se
 * manda `true` a ciegas: si el formulario llegara con el pais vacio por cualquier motivo,
 * un `true` fijo lo traduciria a "todos los paises" y AMPLIARIA el alcance sin que nadie
 * lo pidiera —alguien de Bolivia empezaria a recibir avisos de los 14—. Que la bandera
 * solo aparezca cuando el ambito cambio de verdad cierra esa puerta.
 */
export function actualizarDestinatario(destinatarioId: string, { correo, nombre, cargo, pais, tipoAviso }: Partial<DatosDeDestinatario>, { cambiarAmbito = false }: { cambiarAmbito?: boolean } = {}) {
  return pedir(`/destinatarios-de-aviso/${segUuid(destinatarioId)}`, {
    metodo: 'PUT',
    cuerpo: {
      correo,
      nombre,
      cargo,
      // Editable desde el 06-sep-2026 (decision del operador). No lleva bandera como el
      // ambito: aqui no existe un "sin tipo de aviso" que confundir con la ausencia.
      ...(tipoAviso ? { tipo_aviso: tipoAviso } : {}),
      ...(cambiarAmbito ? { pais: pais || null, cambiar_ambito: true } : {}),
    },
  });
}

/** Deja de recibir avisos SIN borrarse: el historico de a quien se aviso se conserva. */
export function pausarDestinatario(destinatarioId: string) {
  return pedir(`/destinatarios-de-aviso/${segUuid(destinatarioId)}/pausa`, { metodo: 'POST' });
}

export function reactivarDestinatario(destinatarioId: string) {
  return pedir(`/destinatarios-de-aviso/${segUuid(destinatarioId)}/reactivacion`, { metodo: 'POST' });
}

/**
 * HU-47 · A QUIEN SE AVISO DE UN CASO (CP-47-06 y CP-47-10, 5-sep-2026).
 *
 * El endpoint existia desde el principio y nadie lo llamaba, asi que la trazabilidad no
 * se podia abrir por ningun sitio. Los dos casos de prueba quedaron uno bloqueado y el
 * otro fallido con la misma frase: "no hay apartado para visualizarlo".
 *
 * Lo que hace correcta esta lista no esta aqui: esta en que el servidor guarda el correo,
 * el nombre y el cargo COPIADOS en `aviso_enviado`. Por eso dar de baja a un destinatario
 * no reescribe la historia de los avisos ya enviados (RNF-04), que es justo lo que
 * comprueba CP-47-06.
 *
 * Devuelve lista VACIA —no 404— cuando el caso no genero avisos: un borrador nunca los
 * dispara (RN-01), y eso es una respuesta legitima.
 *
 * Lo pueden ver registrador, comite, asuntos regulatorios, aprobador y administrador: no
 * lleva dato de salud, RN-02 garantiza que el aviso nunca lo transporto.
 */
export function consultarAvisosDelCaso(casoId: string) {
  return pedir<RespuestaDelApi[]>(`/avisos?caso_id=${segUuid(casoId)}`);
}

/**
 * HU-10 · LOS CASOS CUYO AVISO NO LLEGO (CP-10-09, 5-sep-2026).
 *
 * "El fallo del aviso queda registrado y VISIBLE PARA ATENCION, porque el tablero es el
 * control, no el correo" (ADR-007). Registrado ya estaba; visible no: habia que abrir el
 * expediente de cada caso, y eso solo funciona si YA se sospecha de ese caso.
 *
 * Es un endpoint PROPIO y no un campo mas en la fila de la bandeja, a proposito: la fila
 * de la bandeja es un contrato cerrado de dato L1 —se sirve sin elevar rol ni dejar
 * asiento de lectura— y anadirle campos obliga a reabrir esa decision (Ley 29733,
 * RNF-03). Por aqui no hace falta: esta respuesta no lleva NADA del caso salvo su numero,
 * que ademas viene copiado de `aviso_enviado` y no de un cruce con `caso`.
 *
 * Devuelve lista vacia cuando no hay ninguno, que es lo normal.
 */
export function avisosFallidos({ limite = 50 }: { limite?: number } = {}) {
  return pedir<Record<string, unknown>[]>(`/avisos/fallidos?limite=${seg(limite)}`);
}

/**
 * Los tipos de aviso que la pantalla ofrece.
 *
 * Desde el 14-sep-2026 el catálogo vive en `lib/tiposDeAviso.ts` —con TODOS los tipos que
 * el backend emite, no solo cuatro— y de ahí leen el desplegable y la tabla. Se reexporta
 * con el nombre de siempre para no romper a quien ya importaba de aquí.
 *
 * ⚠ La lista esta ABIERTA por diseno: `destinatario_aviso.tipo_aviso` es `text` sin
 * CHECK, y el dominio lo dice explicito — un enum obligaria a redesplegar para
 * anadir un tipo, que es justo lo que HU-47 promete evitar. Esto son SUGERENCIAS
 * para el desplegable, no un dominio cerrado.
 */
export { TIPOS_DE_AVISO as TIPOS_DE_AVISO_SUGERIDOS } from '../lib/tiposDeAviso';
